'use client';

export const ANALYTICS_CONSENT_KEY = 'tg_cookie_consent';
export type AnalyticsConsent = 'granted' | 'denied';
const GA4_ID_PATTERN = /^G-[A-Z0-9]+$/;
let consentChoice: AnalyticsConsent | undefined;

export function isAnalyticsPreview(): boolean {
  return typeof window !== 'undefined' && ['/homepage-preview', '/tv/direct', '/tv/probe'].includes(window.location.pathname);
}

/** Retain campaign attribution while dropping tokens, names, and other query data. */
export function analyticsLocation(path: string): string {
  const url = new URL(path, window.location.origin);
  for (const key of Array.from(url.searchParams.keys())) {
    if (!['utm_source', 'utm_medium', 'utm_campaign', 'utm_id', 'utm_content', 'utm_term'].includes(key)) url.searchParams.delete(key);
  }
  url.hash = '';
  return url.toString();
}

/** Initialize the queue synchronously, before any event or consent update can be lost. */
export function ensureGoogleAnalytics(measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID): boolean {
  if (typeof window === 'undefined' || isAnalyticsPreview()) return false;
  try {
    if (measurementId && GA4_ID_PATTERN.test(measurementId) && window.__telegamesGA4 !== measurementId) {
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function () {
        // Google's queue consumes the arguments object used by the standard gtag snippet.
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer!.push(arguments);
      };
      window.__telegamesGA4 = measurementId;
      window.gtag('consent', 'default', {
        analytics_storage: 'denied', ad_storage: 'denied',
        ad_user_data: 'denied', ad_personalization: 'denied',
      });
      try {
        if (!consentChoice) {
          const stored = localStorage.getItem(ANALYTICS_CONSENT_KEY);
          if (stored === 'granted' || stored === 'denied') consentChoice = stored;
        }
      } catch { /* Consent remains denied when storage is unavailable. */ }
      if (consentChoice) window.gtag('consent', 'update', { analytics_storage: consentChoice });
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        send_page_view: false,
        page_location: analyticsLocation(window.location.href),
        page_referrer: document.referrer ? analyticsLocation(document.referrer) : '',
      });
    }
    return typeof window.gtag === 'function';
  } catch { return false; }
}

export function updateAnalyticsConsent(value: AnalyticsConsent): void {
  consentChoice = value;
  try { localStorage.setItem(ANALYTICS_CONSENT_KEY, value); } catch { /* Keep the in-memory choice. */ }
  try { if (ensureGoogleAnalytics()) window.gtag!('consent', 'update', { analytics_storage: value }); } catch { /* A blocked tag does not affect the consent UI. */ }
}

export function hasAnalyticsMeasurementId(): boolean {
  return GA4_ID_PATTERN.test(process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || '');
}
